import React, { useState, useEffect, useCallback } from 'react';

const CurvePopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [popupData, setPopupData] = useState(null);

  // Listen for popup requests
  useEffect(() => {
    const handleShowPopup = (event) => {
      const { action, position, shapeId, lineIndex } = event.detail;
      setPopupData({ action, shapeId, lineIndex });
      setPopupPosition(position);
      setShowPopup(true);
    };

    window.addEventListener('showCurvePopup', handleShowPopup);
    return () => window.removeEventListener('showCurvePopup', handleShowPopup);
  }, []);

  const handleConfirm = useCallback(() => {
    if (popupData) {
      window.dispatchEvent(new CustomEvent('curvePopupResponse', {
        detail: {
          action: popupData.action,
          confirmed: true,
          shapeId: popupData.shapeId,
          lineIndex: popupData.lineIndex
        }
      }));
    }
    setShowPopup(false);
    setPopupData(null);
  }, [popupData]);

  const handleCancel = useCallback(() => {
    if (popupData) {
      window.dispatchEvent(new CustomEvent('curvePopupResponse', {
        detail: {
          action: popupData.action,
          confirmed: false,
          shapeId: popupData.shapeId,
          lineIndex: popupData.lineIndex
        }
      }));
    }
    setShowPopup(false);
    setPopupData(null);
  }, [popupData]);

  // Handle popup outside click and keyboard events
  useEffect(() => {
    if (!showPopup) return;

    const handleClickOutside = (event) => {
      // Check if the click is outside our popup
      if (!event.target.closest('[data-curve-popup]')) {
        handleCancel();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCancel();
      } else if (event.key === 'Enter') {
        handleConfirm();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPopup, handleConfirm, handleCancel]);

  if (!showPopup || !popupData) return null;

  const popupStyle = {
    position: 'fixed',
    left: `${popupPosition.x}px`,
    top: `${popupPosition.y}px`,
    background: 'white',
    border: '2px solid #4a90e2',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    minWidth: '200px'
  };

  const buttonStyle = {
    margin: '0 8px',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold'
  };

  const confirmButtonStyle = {
    ...buttonStyle,
    background: '#4a90e2',
    color: 'white'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    background: '#f0f0f0',
    color: '#333'
  };

  return (
    <div style={popupStyle} data-curve-popup>
      <div style={{ marginBottom: '12px', fontWeight: 'bold', color: '#333' }}>
        {popupData.action === 'activate' ? 'Activate Curve Mode?' : 'Exit Curve Mode?'}
      </div>
      <div style={{ marginBottom: '16px', color: '#666', fontSize: '12px' }}>
        {popupData.action === 'activate' 
          ? 'This will allow you to create curved lines with control points.'
          : 'This will convert the curve back to a straight line.'}
      </div>
      <div style={{ textAlign: 'right' }}>
        <button 
          style={cancelButtonStyle}
          onClick={handleCancel}
          onMouseOver={(e) => e.target.style.background = '#e0e0e0'}
          onMouseOut={(e) => e.target.style.background = '#f0f0f0'}
        >
          Cancel
        </button>
        <button 
          style={confirmButtonStyle}
          onClick={handleConfirm}
          onMouseOver={(e) => e.target.style.background = '#3a7bc8'}
          onMouseOut={(e) => e.target.style.background = '#4a90e2'}
        >
          {popupData.action === 'activate' ? 'Activate' : 'Exit'}
        </button>
      </div>
    </div>
  );
};

export default CurvePopup; 